/**
 * "Ask this scene": the visitor-facing Q&A guide (uiOptions.sceneGuide).
 *
 * viewerUI places the chrome (an Ask button in the toolbar and an empty `.storysplat-guide-panel`);
 * this module renders the chat into the panel and runs the conversation against the StorySplat
 * sceneGuide endpoint:
 *
 *   POST <endpoint>  { sceneId, messages: [{ role, content }], waypointIndex? }
 *   200 text/event-stream:  data: {"type":"delta","text":"…"} … data: {"type":"done"}
 *                           data: {"type":"error","code","message"}   (mid-stream failure)
 *   non-2xx JSON:           { "error": { "code", "message" } }
 *
 * The conversation lives in memory only. Built with DOM APIs (textContent), so neither the
 * creator's greeting / suggestions nor the model's answers can inject markup.
 */
import type { SceneGuideConfig } from '../types';
import { attachSheetDrag, COMPACT_EMBED_WIDTH, SCENE_GUIDE_ICON, SCENE_GUIDE_LABELS } from './viewerUI';

export const DEFAULT_SCENE_GUIDE_ENDPOINT = 'https://us-central1-story-splat.cloudfunctions.net/sceneGuide';
/** Request limits, mirrored from the sceneGuide function so a long conversation is trimmed, not refused. */
export const SCENE_GUIDE_MAX_MESSAGES = 12;
export const SCENE_GUIDE_MAX_USER_CHARS = 1000;
export const SCENE_GUIDE_MAX_ASSISTANT_CHARS = 2000;
export const SCENE_GUIDE_MAX_TOTAL_CHARS = 12000;
export const SCENE_GUIDE_MAX_SUGGESTIONS = 4;
const MAX_NAME_CHARS = 40;
const MAX_GREETING_CHARS = 500;
const MAX_SUGGESTION_CHARS = 200;
const MAX_ERROR_CHARS = 300;

export interface SceneGuideMessage {
    role: 'user' | 'assistant';
    content: string;
}

export interface SceneGuideRequestBody {
    sceneId: string;
    messages: SceneGuideMessage[];
    waypointIndex?: number;
    /** JPEG of the current viewer frame, attached only to this question. */
    screenshot?: string;
}

/** A guide the viewer will show: enabled, in a published scene with an id, defaults filled in. */
export interface ResolvedSceneGuide {
    sceneId: string;
    name: string;
    greeting: string;
    suggestedQuestions: string[];
}

const cleanText = (value: unknown, max: number): string =>
    typeof value === 'string' ? value.trim().slice(0, max) : '';

/**
 * Visibility rules: only when the scene enables the guide (`enabled === true`, nothing looser),
 * the scene has an id for the endpoint to look it up by, and the viewer is not the editor.
 * Returns null whenever the guide must not appear.
 */
export function resolveSceneGuide(
    config: SceneGuideConfig | null | undefined,
    context: { sceneId?: string | null; isEditorMode: boolean },
): ResolvedSceneGuide | null {
    if (!config || typeof config !== 'object' || config.enabled !== true) return null;
    if (context.isEditorMode) return null;
    const sceneId = typeof context.sceneId === 'string' ? context.sceneId.trim() : '';
    if (!sceneId) return null;
    const suggestedQuestions: string[] = [];
    if (Array.isArray(config.suggestedQuestions)) {
        for (const raw of config.suggestedQuestions) {
            const question = cleanText(raw, MAX_SUGGESTION_CHARS);
            if (question && !suggestedQuestions.includes(question)) suggestedQuestions.push(question);
            if (suggestedQuestions.length === SCENE_GUIDE_MAX_SUGGESTIONS) break;
        }
    }
    return {
        sceneId,
        name: cleanText(config.name, MAX_NAME_CHARS) || SCENE_GUIDE_LABELS.defaultName,
        greeting: cleanText(config.greeting, MAX_GREETING_CHARS) || SCENE_GUIDE_LABELS.greeting,
        suggestedQuestions,
    };
}

/**
 * The request body: the last 12 messages, user text trimmed to 1000 characters (assistant text
 * to 2000), and the oldest turns dropped while the whole exceeds the function's 12 000-character
 * cap. `waypointIndex` only when it is a whole, non-negative number.
 */
export function buildSceneGuideRequestBody(
    sceneId: string,
    messages: readonly SceneGuideMessage[],
    waypointIndex?: number | null,
    screenshot?: string | null,
): SceneGuideRequestBody {
    const cleaned: SceneGuideMessage[] = [];
    for (const message of messages) {
        if (!message || (message.role !== 'user' && message.role !== 'assistant') || typeof message.content !== 'string') continue;
        const max = message.role === 'user' ? SCENE_GUIDE_MAX_USER_CHARS : SCENE_GUIDE_MAX_ASSISTANT_CHARS;
        const content = message.content.trim().slice(0, max);
        if (content) cleaned.push({ role: message.role, content });
    }
    let recent = cleaned.slice(-SCENE_GUIDE_MAX_MESSAGES);
    let total = recent.reduce((sum, message) => sum + message.content.length, 0);
    while (recent.length > 1 && total > SCENE_GUIDE_MAX_TOTAL_CHARS) {
        total -= recent[0].content.length;
        recent = recent.slice(1);
    }
    const body: SceneGuideRequestBody = { sceneId, messages: recent };
    if (typeof waypointIndex === 'number' && Number.isSafeInteger(waypointIndex) && waypointIndex >= 0) {
        body.waypointIndex = waypointIndex;
    }
    if (typeof screenshot === 'string' && screenshot.startsWith('data:image/jpeg;base64,') && screenshot.length <= 512 * 1024) body.screenshot = screenshot;
    return body;
}

export type SceneGuideStreamEvent =
    | { type: 'delta'; text: string }
    | { type: 'done' }
    | { type: 'error'; code?: string; message?: string };

/** One SSE `data` payload → a guide event, or null for anything unrecognised. */
export function parseSceneGuideEventData(data: string): SceneGuideStreamEvent | null {
    let parsed: unknown;
    try { parsed = JSON.parse(data); } catch { return null; }
    if (!parsed || typeof parsed !== 'object') return null;
    const event = parsed as Record<string, unknown>;
    if (event.type === 'delta') return typeof event.text === 'string' ? { type: 'delta', text: event.text } : null;
    if (event.type === 'done') return { type: 'done' };
    if (event.type === 'error') {
        return {
            type: 'error',
            code: typeof event.code === 'string' ? event.code : undefined,
            message: typeof event.message === 'string' ? event.message : undefined,
        };
    }
    return null;
}

export interface SceneGuideSseParser {
    /** Feed decoded text; frames may be split anywhere, including between CR and LF. */
    push(chunk: string): void;
    /** End of stream: dispatch a final frame that had no trailing blank line. */
    flush(): void;
}

/**
 * Incremental text/event-stream parser (the subset the guide uses): LF, CRLF or CR line ends,
 * events end at a blank line, multi-line `data:` joined with "\n", comments and other fields
 * ignored.
 */
export function createSceneGuideSseParser(onEvent: (event: SceneGuideStreamEvent) => void): SceneGuideSseParser {
    let buffer = '';
    let dataLines: string[] = [];
    let started = false;
    const dispatch = () => {
        if (dataLines.length === 0) return;
        const event = parseSceneGuideEventData(dataLines.join('\n'));
        dataLines = [];
        if (event) onEvent(event);
    };
    const processLine = (line: string) => {
        if (line === '') { dispatch(); return; }
        if (line.startsWith(':')) return;
        const colon = line.indexOf(':');
        const field = colon === -1 ? line : line.slice(0, colon);
        if (field !== 'data') return;
        let value = colon === -1 ? '' : line.slice(colon + 1);
        if (value.startsWith(' ')) value = value.slice(1);
        dataLines.push(value);
    };
    return {
        push(chunk: string) {
            if (!chunk) return;
            if (!started) {
                started = true;
                if (chunk.charCodeAt(0) === 0xfeff) chunk = chunk.slice(1);
            }
            buffer += chunk;
            let start = 0;
            for (let i = 0; i < buffer.length; i++) {
                const ch = buffer[i];
                if (ch !== '\n' && ch !== '\r') continue;
                // A CR at the very end may be the first half of a CRLF split across chunks.
                if (ch === '\r' && i === buffer.length - 1) break;
                processLine(buffer.slice(start, i));
                if (ch === '\r' && buffer[i + 1] === '\n') i++;
                start = i + 1;
            }
            buffer = buffer.slice(start);
        },
        flush() {
            if (buffer) {
                const rest = buffer.endsWith('\r') ? buffer.slice(0, -1) : buffer;
                buffer = '';
                processLine(rest);
            }
            dispatch();
        },
    };
}

export type SceneGuideAskResult =
    | { status: 'done' }
    | { status: 'aborted' }
    | { status: 'error'; message: string; code?: string };

export interface SceneGuideAskOptions {
    endpoint: string;
    body: SceneGuideRequestBody;
    onDelta: (text: string) => void;
    signal?: AbortSignal;
    fetchImpl?: typeof fetch;
}

const visitorMessage = (value: unknown): string | null => {
    if (typeof value !== 'string') return null;
    const message = value.trim();
    return message ? message.slice(0, MAX_ERROR_CHARS) : null;
};

const isAbortError = (error: unknown): boolean =>
    !!error && typeof error === 'object' && (error as { name?: unknown }).name === 'AbortError';

/**
 * Post one question and stream the answer. Resolves (never rejects) with how it ended; deltas
 * arrive through `onDelta` as they stream in.
 */
export async function askSceneGuide(options: SceneGuideAskOptions): Promise<SceneGuideAskResult> {
    const { endpoint, body, onDelta, signal } = options;
    const fetchFn = options.fetchImpl ?? (typeof fetch === 'function' ? fetch.bind(globalThis) : undefined);
    if (!fetchFn) return { status: 'error', message: SCENE_GUIDE_LABELS.errorNetwork };
    let response: Response;
    try {
        response = await fetchFn(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
            body: JSON.stringify(body),
            signal,
        });
    } catch (error) {
        if (isAbortError(error) || signal?.aborted) return { status: 'aborted' };
        return { status: 'error', message: SCENE_GUIDE_LABELS.errorNetwork };
    }
    if (!response.ok) {
        let message: string | null = null;
        let code: string | undefined;
        try {
            const payload = await response.json() as { error?: { code?: unknown; message?: unknown } } | null;
            message = visitorMessage(payload?.error?.message);
            if (typeof payload?.error?.code === 'string') code = payload.error.code;
        } catch {
            if (signal?.aborted) return { status: 'aborted' };
        }
        return { status: 'error', message: message ?? SCENE_GUIDE_LABELS.errorGeneric, ...(code ? { code } : {}) };
    }

    let done = false;
    let received = false;
    let failure: { message: string; code?: string } | null = null;
    const parser = createSceneGuideSseParser((event) => {
        if (done || failure) return;
        if (event.type === 'delta') {
            if (!event.text) return;
            received = true;
            onDelta(event.text);
        } else if (event.type === 'done') {
            done = true;
        } else {
            failure = { message: visitorMessage(event.message) ?? SCENE_GUIDE_LABELS.errorGeneric, ...(event.code ? { code: event.code } : {}) };
        }
    });
    try {
        const reader = response.body && typeof response.body.getReader === 'function' ? response.body.getReader() : null;
        if (reader) {
            const decoder = new TextDecoder();
            while (!done && !failure) {
                const { value, done: streamEnded } = await reader.read();
                if (streamEnded) break;
                if (value) parser.push(decoder.decode(value, { stream: true }));
            }
            if (done || failure) {
                // The answer is complete (or failed): stop reading, let the connection go.
                try { reader.cancel().catch(() => { /* already closed */ }); } catch { /* already closed */ }
            } else {
                parser.push(decoder.decode());
            }
        } else {
            parser.push(await response.text());
        }
        parser.flush();
    } catch (error) {
        if (isAbortError(error) || signal?.aborted) return { status: 'aborted' };
        if (!done && !failure) {
            return { status: 'error', message: received ? SCENE_GUIDE_LABELS.errorInterrupted : SCENE_GUIDE_LABELS.errorNetwork };
        }
    }
    if (failure) return { status: 'error', ...(failure as { message: string; code?: string }) };
    if (done) return { status: 'done' };
    if (signal?.aborted) return { status: 'aborted' };
    return { status: 'error', message: received ? SCENE_GUIDE_LABELS.errorInterrupted : SCENE_GUIDE_LABELS.errorGeneric };
}

export interface SceneGuideMountOptions {
    guide: ResolvedSceneGuide;
    /** Toolbar Ask button (viewerUI `.storysplat-guide-btn`). */
    button: HTMLButtonElement;
    /** Empty panel the chat is rendered into (viewerUI `.storysplat-guide-panel`). */
    panel: HTMLElement;
    /** The viewer container: its width decides floating panel vs bottom sheet. */
    container: HTMLElement;
    endpoint?: string;
    /** Current tour waypoint, when the visitor is on the tour. */
    getWaypointIndex?: () => number | null | undefined;
    /** Capture the current rendered scene, without surrounding page UI. Failures fall back to text. */
    captureScreenshot?: () => Promise<string>;
    /** Called when the panel opens; `asSheet` is true for the narrow-embed bottom sheet. */
    onOpen?: (asSheet: boolean) => void;
    fetchImpl?: typeof fetch;
}

export interface SceneGuideHandle {
    setOpen(open: boolean): void;
    isOpen(): boolean;
    /** Stops any answer in flight and releases listeners, observers and the scrim. */
    destroy(): void;
}

const CLOSE_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
const SEND_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 19V5M5.5 11.5 12 5l6.5 6.5"/></svg>';
const STOP_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="7" y="7" width="10" height="10" rx="2" fill="currentColor"/></svg>';

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] => {
    const node = document.createElement(tag);
    node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
};

export function mountSceneGuide(options: SceneGuideMountOptions): SceneGuideHandle {
    const { guide, button, panel, container } = options;
    const endpoint = options.endpoint || DEFAULT_SCENE_GUIDE_ENDPOINT;
    const label = SCENE_GUIDE_LABELS.ask.replace('{name}', guide.name);
    const history: SceneGuideMessage[] = [];
    let open = false;
    let sheet = false;
    let busy = false;
    let destroyed = false;
    let inFlight: AbortController | null = null;

    // ── DOM ──────────────────────────────────────────────────────────────────
    panel.replaceChildren();
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', label);
    panel.tabIndex = -1;
    const handle = el('div', 'storysplat-guide-handle');
    handle.setAttribute('aria-hidden', 'true');
    const header = el('div', 'storysplat-guide-header');
    const avatar = el('span', 'storysplat-guide-avatar');
    avatar.setAttribute('aria-hidden', 'true');
    avatar.innerHTML = SCENE_GUIDE_ICON;
    const title = el('span', 'storysplat-guide-title', guide.name);
    const closeButton = el('button', 'storysplat-guide-close');
    closeButton.type = 'button';
    closeButton.setAttribute('aria-label', SCENE_GUIDE_LABELS.close);
    closeButton.setAttribute('title', SCENE_GUIDE_LABELS.close);
    closeButton.innerHTML = CLOSE_ICON;
    header.append(avatar, title, closeButton);
    const messages = el('div', 'storysplat-guide-messages');
    messages.setAttribute('aria-label', SCENE_GUIDE_LABELS.conversation);
    messages.appendChild(el('div', 'storysplat-guide-msg storysplat-guide-msg--assistant', guide.greeting));
    const suggestions = el('div', 'storysplat-guide-suggestions');
    suggestions.setAttribute('role', 'group');
    suggestions.setAttribute('aria-label', SCENE_GUIDE_LABELS.suggestions);
    for (const question of guide.suggestedQuestions) {
        const chip = el('button', 'storysplat-guide-chip', question);
        chip.type = 'button';
        chip.addEventListener('click', () => { void ask(question); });
        suggestions.appendChild(chip);
    }
    suggestions.hidden = guide.suggestedQuestions.length === 0;
    const compose = el('div', 'storysplat-guide-compose');
    const input = el('textarea', 'storysplat-guide-input');
    input.rows = 1;
    input.maxLength = SCENE_GUIDE_MAX_USER_CHARS;
    input.placeholder = SCENE_GUIDE_LABELS.placeholder;
    input.setAttribute('aria-label', SCENE_GUIDE_LABELS.input);
    input.setAttribute('enterkeyhint', 'send');
    const sendButton = el('button', 'storysplat-guide-send');
    sendButton.type = 'button';
    compose.append(input, sendButton);
    const footnote = el('p', 'storysplat-guide-footnote', SCENE_GUIDE_LABELS.footnote);
    // Streaming text is not announced token by token; finished answers and errors are.
    const announcer = el('div', 'storysplat-guide-sr');
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('role', 'status');
    panel.append(handle, header, messages, suggestions, compose, footnote, announcer);

    const scrim = el('div', 'storysplat-guide-scrim');
    scrim.setAttribute('aria-hidden', 'true');
    container.insertBefore(scrim, panel.parentNode === container ? panel : null);

    // ── State ────────────────────────────────────────────────────────────────
    const announce = (text: string) => { announcer.textContent = text; };
    const nearBottom = () => messages.scrollHeight - messages.scrollTop - messages.clientHeight < 32;
    const scrollToBottom = () => { messages.scrollTop = messages.scrollHeight; };
    const autosize = () => {
        input.style.height = 'auto';
        if (input.scrollHeight > 0) input.style.height = `${Math.min(input.scrollHeight, 96)}px`;
        else input.style.removeProperty('height');
    };
    const syncSend = () => {
        if (busy) {
            sendButton.disabled = false;
            sendButton.innerHTML = STOP_ICON;
            sendButton.setAttribute('aria-label', SCENE_GUIDE_LABELS.stop);
            sendButton.setAttribute('title', SCENE_GUIDE_LABELS.stop);
        } else {
            sendButton.disabled = input.value.trim().length === 0;
            sendButton.innerHTML = SEND_ICON;
            sendButton.setAttribute('aria-label', SCENE_GUIDE_LABELS.send);
            sendButton.setAttribute('title', SCENE_GUIDE_LABELS.send);
        }
    };
    const setBusy = (next: boolean) => {
        busy = next;
        messages.setAttribute('aria-busy', String(next));
        suggestions.querySelectorAll('button').forEach((chip) => { chip.disabled = next; });
        syncSend();
    };
    const updateLayout = () => {
        const width = container.clientWidth || (typeof window !== 'undefined' ? window.innerWidth : 0);
        sheet = width > 0 && width <= COMPACT_EMBED_WIDTH;
        panel.classList.toggle('storysplat-guide-panel--sheet', sheet);
        scrim.classList.toggle('visible', open && sheet);
    };

    async function ask(raw: string): Promise<void> {
        const question = raw.trim().slice(0, SCENE_GUIDE_MAX_USER_CHARS);
        if (!question || busy || destroyed) return;
        suggestions.hidden = true;
        messages.appendChild(el('div', 'storysplat-guide-msg storysplat-guide-msg--user', question));
        history.push({ role: 'user', content: question });
        const bubble = el('div', 'storysplat-guide-msg storysplat-guide-msg--assistant is-pending');
        for (let i = 0; i < 3; i++) bubble.appendChild(el('span', 'storysplat-guide-dot')).setAttribute('aria-hidden', 'true');
        bubble.appendChild(el('span', 'storysplat-guide-sr', SCENE_GUIDE_LABELS.thinking));
        messages.appendChild(bubble);
        scrollToBottom();
        announce(SCENE_GUIDE_LABELS.thinking);
        setBusy(true);

        const controller = new AbortController();
        inFlight = controller;
        let answer = '';
        let waypointIndex: number | null | undefined;
        try { waypointIndex = options.getWaypointIndex?.(); } catch { waypointIndex = undefined; }
        let screenshot: string | undefined;
        if (options.captureScreenshot) {
            try { screenshot = await options.captureScreenshot(); } catch { /* A missing frame must not prevent text questions. */ }
        }
        if (destroyed) return;
        const result: SceneGuideAskResult = controller.signal.aborted ? { status: 'aborted' } : await askSceneGuide({
            endpoint,
            body: buildSceneGuideRequestBody(guide.sceneId, history, waypointIndex, screenshot),
            signal: controller.signal,
            fetchImpl: options.fetchImpl,
            onDelta: (text) => {
                if (destroyed) return;
                const pinned = nearBottom();
                if (!answer) bubble.classList.remove('is-pending');
                answer += text;
                bubble.textContent = answer;
                if (pinned) scrollToBottom();
            },
        });
        if (inFlight === controller) inFlight = null;
        if (destroyed) return;

        const pinned = nearBottom();
        if (answer) {
            history.push({ role: 'assistant', content: answer });
        } else {
            bubble.remove();
            // Unanswered: keep it on screen, but out of the next request so turns still alternate.
            const last = history[history.length - 1];
            if (last?.role === 'user' && last.content === question) history.pop();
        }
        if (result.status === 'error') {
            messages.appendChild(el('div', 'storysplat-guide-error', result.message));
            announce(result.message);
        } else if (result.status === 'done') {
            if (answer) announce(answer);
            else {
                messages.appendChild(el('div', 'storysplat-guide-error', SCENE_GUIDE_LABELS.errorGeneric));
                announce(SCENE_GUIDE_LABELS.errorGeneric);
            }
        } else {
            announce('');
        }
        if (pinned) scrollToBottom();
        setBusy(false);
    }

    const submit = () => {
        if (busy) return;
        const text = input.value;
        if (!text.trim()) return;
        input.value = '';
        autosize();
        void ask(text);
    };

    const setOpen = (next: boolean) => {
        if (destroyed || next === open) return;
        open = next;
        updateLayout();
        panel.classList.toggle('open', open);
        button.setAttribute('aria-expanded', String(open));
        scrim.classList.toggle('visible', open && sheet);
        if (open) {
            options.onOpen?.(sheet);
            scrollToBottom();
            // Desktop: straight to the question field. Phones: focus the panel instead, so the
            // keyboard doesn't jump up over the sheet before the visitor has read anything.
            try { (sheet ? panel : input).focus({ preventScroll: true }); } catch { /* not focusable */ }
        } else if (panel.contains(document.activeElement)) {
            try { button.focus({ preventScroll: true }); } catch { /* not focusable */ }
        }
    };

    // ── Events ───────────────────────────────────────────────────────────────
    const onButtonClick = () => setOpen(!open);
    button.addEventListener('click', onButtonClick);
    closeButton.addEventListener('click', () => setOpen(false));
    scrim.addEventListener('click', () => setOpen(false));
    sendButton.addEventListener('click', () => {
        if (busy) inFlight?.abort();
        else submit();
    });
    input.addEventListener('input', () => { autosize(); syncSend(); });
    input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' || event.shiftKey || event.isComposing || event.keyCode === 229) return;
        event.preventDefault();
        submit();
    });
    panel.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            setOpen(false);
        }
        // Typing here must not reach the viewer's keyboard shortcuts (movement, Escape handlers).
        event.stopPropagation();
    });
    // Keep panel and scrim interaction from steering the camera behind them.
    for (const target of [panel, scrim]) {
        for (const type of ['pointerdown', 'touchstart', 'wheel'] as const) {
            target.addEventListener(type, (event) => event.stopPropagation(), { passive: true });
        }
    }
    attachSheetDrag(panel, handle, () => sheet, () => setOpen(false));

    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(() => updateLayout());
        observer.observe(container);
    }
    const onWindowResize = () => updateLayout();
    window.addEventListener('resize', onWindowResize);
    updateLayout();
    syncSend();

    return {
        setOpen,
        isOpen: () => open,
        destroy() {
            if (destroyed) return;
            setOpen(false);
            destroyed = true;
            inFlight?.abort();
            inFlight = null;
            history.length = 0;
            observer?.disconnect();
            observer = null;
            window.removeEventListener('resize', onWindowResize);
            button.removeEventListener('click', onButtonClick);
            button.setAttribute('aria-expanded', 'false');
            scrim.remove();
            panel.replaceChildren();
            panel.classList.remove('open', 'storysplat-guide-panel--sheet');
        },
    };
}

/** Encode a fresh scene frame for Ask without retaining an image in chat history. */
export async function encodeSceneGuideScreenshot(blob: Blob): Promise<string> {
    const image = await createImageBitmap(blob);
    try {
        const scale = Math.min(1, 1280 / Math.max(image.width, image.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.width * scale));
        canvas.height = Math.max(1, Math.round(image.height * scale));
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Screenshot canvas unavailable');
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        for (const quality of [0.72, 0.5, 0.3]) {
            const data = canvas.toDataURL('image/jpeg', quality);
            if (data.startsWith('data:image/jpeg;base64,') && data.length <= 512 * 1024) return data;
        }
        throw new Error('Screenshot exceeds request limit');
    } finally { image.close(); }
}
